import { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, formatApiErrorDetail } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { useLanguage } from "@/context/LanguageContext";
import { googleCalendarUrl } from "@/lib/calendar";
import { toast } from "sonner";
import { Loader2, MailCheck, CalendarPlus, CreditCard, PlaneTakeoff, Phone, MapPin } from "lucide-react";

export default function Portal() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { t } = useLanguage();

  if (token) return <PortalDashboard token={token} t={t} />;
  return <PortalRequest t={t} />;
}

function PortalRequest({ t }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/portal/request-link`, { email, origin: window.location.origin });
      setSent(true);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-background text-foreground min-h-screen">
      <Navbar />
      <div className="max-w-md mx-auto px-6 pt-40 pb-24">
        {sent ? (
          <div className="text-center" data-testid="portal-link-sent">
            <div className="h-16 w-16 rounded-full border border-white/20 bg-white/10 flex items-center justify-center mx-auto mb-6">
              <MailCheck className="h-8 w-8 text-white" />
            </div>
            <h1 className="font-serif font-light text-3xl text-white">{t("portal.sent")}</h1>
            <p className="text-muted-foreground mt-4">{t("portal.sentSub")}</p>
          </div>
        ) : (
          <>
            <p className="text-xs tracking-luxe uppercase text-muted-foreground mb-4">{t("portal.eyebrow")}</p>
            <h1 className="font-serif font-light text-4xl text-white mb-3">{t("portal.title")}</h1>
            <p className="text-muted-foreground mb-8">{t("portal.subtitle")}</p>
            <form onSubmit={submit} className="space-y-4" data-testid="portal-request-form">
              <div>
                <Label className="text-xs uppercase tracking-luxe text-muted-foreground">{t("portal.email")}</Label>
                <Input data-testid="portal-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  className="mt-2 h-12 bg-secondary border-white/10 text-white" />
              </div>
              <Button data-testid="portal-send-link" type="submit" disabled={loading}
                className="w-full h-12 rounded-full bg-white text-black hover:bg-white/85">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("portal.sendLink")}
              </Button>
            </form>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}

function PortalDashboard({ token, t }) {
  const client = useMemo(() => axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } }), [token]);
  const [state, setState] = useState({ loading: true, error: null, bookings: [], email: "" });

  const refresh = useCallback(async () => {
    try {
      const { data } = await client.get("/portal/bookings");
      setState({ loading: false, error: null, bookings: data.bookings, email: data.email });
    } catch (err) {
      setState({ loading: false, error: err.response?.data?.detail || t("portal.expired"), bookings: [], email: "" });
    }
  }, [client, t]);
  useEffect(() => { refresh(); }, [refresh]);

  if (state.loading) {
    return (
      <div className="bg-background min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-white" />
      </div>
    );
  }

  return (
    <div className="bg-background text-foreground min-h-screen">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 pt-32 pb-24">
        <p className="text-xs tracking-luxe uppercase text-muted-foreground mb-3">{t("portal.eyebrow")}</p>
        <h1 className="font-serif font-light text-4xl sm:text-5xl text-white mb-2">{t("portal.yourBookings")}</h1>
        {state.email && <p className="text-muted-foreground mb-10">{state.email}</p>}

        {state.error && (
          <div className="rounded-lg border border-white/10 bg-card p-8 text-center" data-testid="portal-error">
            <p className="text-white/80">{state.error}</p>
          </div>
        )}

        {!state.error && state.bookings.length === 0 && (
          <p className="text-muted-foreground">{t("portal.none")}</p>
        )}

        <div className="space-y-6">
          {state.bookings.map((b) => (
            <PortalBookingCard key={b.id} b={b} client={client} t={t} onChange={refresh} />
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
}

function PortalBookingCard({ b, client, t, onChange }) {
  const [flight, setFlight] = useState({ flight_number: b.flight_number || "", airline: b.airline || "", terminal: b.terminal || "" });
  const [contact, setContact] = useState({ phone: b.phone || "", special_requests: b.special_requests || "" });
  const [reqMsg, setReqMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const balance = b.balance_due || 0;

  const saveFlight = async () => {
    setBusy(true);
    try { await client.patch(`/portal/bookings/${b.id}/flight`, flight); toast.success(t("portal.saved")); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const saveContact = async () => {
    setBusy(true);
    try { await client.patch(`/portal/bookings/${b.id}/contact`, contact); toast.success(t("portal.saved")); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const payBalance = async () => {
    setBusy(true);
    try {
      const { data } = await client.post(`/portal/bookings/${b.id}/pay-balance`, { origin: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setBusy(false); }
  };
  const sendRequest = async (type) => {
    setBusy(true);
    try {
      await client.post(`/portal/bookings/${b.id}/request`, { type, message: reqMsg });
      toast.success(t("portal.reqSent")); setReqMsg(""); onChange();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const statusLabel = (b.payment_status === "paid") ? t("portal.paid")
    : (b.payment_status === "deposit_paid") ? t("portal.depositPaid")
    : (b.quote_required || b.payment_status === "quote_requested") ? t("account.quoteRequested")
    : t("portal.unpaid");

  return (
    <div className="rounded-xl border border-white/10 bg-card overflow-hidden" data-testid={`portal-booking-${b.id}`}>
      <div className="p-6 border-b border-white/10 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="font-serif text-2xl text-white">{b.service_label || b.vehicle}</h3>
          <p className="text-muted-foreground text-sm mt-1">{b.reservation_number || b.id}</p>
          <div className="flex items-center gap-2 text-white/80 text-sm mt-3"><MapPin className="h-4 w-4 text-muted-foreground" /> {b.pickup_location} → {b.dropoff_location || "—"}</div>
          <p className="text-white/70 text-sm mt-1">{b.date} {b.time} · {b.passengers} pax</p>
        </div>
        <span className="text-[11px] uppercase tracking-luxe px-3 py-1 rounded-full border border-white/20 text-white/80">{statusLabel}</span>
      </div>

      <div className="p-6 grid md:grid-cols-2 gap-6">
        {/* Flight */}
        <div>
          <p className="text-xs uppercase tracking-luxe text-muted-foreground mb-3 flex items-center gap-2"><PlaneTakeoff className="h-4 w-4" /> {t("portal.flight.title")}</p>
          <div className="space-y-2">
            <Input data-testid={`portal-flight-${b.id}`} placeholder={t("portal.flight.number")} value={flight.flight_number}
              onChange={(e) => setFlight({ ...flight, flight_number: e.target.value })} className="h-11 bg-secondary border-white/10 text-white" />
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder={t("portal.flight.airline")} value={flight.airline}
                onChange={(e) => setFlight({ ...flight, airline: e.target.value })} className="h-11 bg-secondary border-white/10 text-white" />
              <Input placeholder={t("portal.flight.terminal")} value={flight.terminal}
                onChange={(e) => setFlight({ ...flight, terminal: e.target.value })} className="h-11 bg-secondary border-white/10 text-white" />
            </div>
            <Button data-testid={`portal-save-flight-${b.id}`} variant="outline" onClick={saveFlight} disabled={busy}
              className="w-full rounded-full border-white/20 bg-transparent text-white hover:bg-white/10">{t("portal.save")}</Button>
          </div>
        </div>
        {/* Contact */}
        <div>
          <p className="text-xs uppercase tracking-luxe text-muted-foreground mb-3 flex items-center gap-2"><Phone className="h-4 w-4" /> {t("portal.contact.title")}</p>
          <div className="space-y-2">
            <Input data-testid={`portal-contact-${b.id}`} placeholder={t("portal.contact.phone")} value={contact.phone}
              onChange={(e) => setContact({ ...contact, phone: e.target.value })} className="h-11 bg-secondary border-white/10 text-white" />
            <Textarea placeholder={t("portal.contact.notes")} value={contact.special_requests}
              onChange={(e) => setContact({ ...contact, special_requests: e.target.value })} className="bg-secondary border-white/10 text-white min-h-[44px]" />
            <Button data-testid={`portal-save-contact-${b.id}`} variant="outline" onClick={saveContact} disabled={busy}
              className="w-full rounded-full border-white/20 bg-transparent text-white hover:bg-white/10">{t("portal.save")}</Button>
          </div>
        </div>
      </div>

      <div className="p-6 pt-0 flex flex-wrap gap-3">
        {balance > 0 && (
          <Button data-testid={`portal-pay-balance-${b.id}`} onClick={payBalance} disabled={busy}
            className="rounded-full bg-white text-black hover:bg-white/85">
            <CreditCard className="h-4 w-4 mr-2" /> {t("portal.payBalance").replace("{amount}", balance)}
          </Button>
        )}
        <a href={googleCalendarUrl(b)} target="_blank" rel="noopener noreferrer">
          <Button variant="outline" className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/10">
            <CalendarPlus className="h-4 w-4 mr-2" /> {t("portal.addCalendar")}
          </Button>
        </a>
        <RequestDialog type="modification" label={t("portal.reqMod")} desc={t("portal.reqModDesc")}
          reqMsg={reqMsg} setReqMsg={setReqMsg} onSend={() => sendRequest("modification")} busy={busy} t={t} testid={`portal-mod-${b.id}`} />
        <RequestDialog type="cancellation" label={t("portal.reqCancel")} desc={t("portal.reqCancelDesc")}
          reqMsg={reqMsg} setReqMsg={setReqMsg} onSend={() => sendRequest("cancellation")} busy={busy} t={t} testid={`portal-cancel-${b.id}`} />
      </div>
    </div>
  );
}

function RequestDialog({ label, desc, reqMsg, setReqMsg, onSend, busy, t, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" data-testid={testid} className="rounded-full text-muted-foreground hover:text-white hover:bg-white/5">{label}</Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-white/10 text-white">
        <DialogHeader><DialogTitle className="font-serif font-light text-2xl">{label}</DialogTitle></DialogHeader>
        <p className="text-muted-foreground text-sm">{desc}</p>
        <Textarea data-testid={`${testid}-msg`} value={reqMsg} onChange={(e) => setReqMsg(e.target.value)}
          placeholder={t("portal.reqMsg")} className="bg-secondary border-white/10 text-white min-h-24" />
        <DialogFooter>
          <Button data-testid={`${testid}-send`} onClick={() => { onSend(); setOpen(false); }} disabled={busy}
            className="rounded-full bg-white text-black hover:bg-white/85">{t("portal.reqSend")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
